import MapView, { MapViewProps } from '@cas/components/maps/map-view/MapView';
import MapLoader from '@cas/components/maps//map-loader/MapLoader';
import {
  GeofenceFilters,
  GeofenceProperties,
  useGetGeofencesGeoJsonQuery,
} from '@casops/store/apis/geofencesApi';
import useMapProvider from '../hooks/useMapProvider';
import OtherGeofencesLayerGroup from '../layers/other-geofences-layer-group/OtherGeofencesLayerGroup';
import { ReactElement, useMemo } from 'react';
import { latLng, LatLngBounds, latLngBounds } from 'leaflet';
import PersistedLocation from '../layers/persisted-location/PersistedLocation';
import { useMapLastPositionData } from '../hooks/useMapLastPositionData';
import { Feature, Geometry, Position } from 'geojson';
import { useSearchParamId } from '@casops/shared/hooks/useSearchParamId';
import { useMatch, useParams } from 'react-router-dom';
import ShapeEditor from './shape-editor/ShapeEditor';
import styles from './SiteMap.module.scss';
import { ASSET_KEY, GEOFENCE_KEY, GEOFENCE_SUB_ROUTE } from '@casops/navigation';
import MultipleAssetPositionLayer from '../multipe-asset-pos/MultipleAssetPositionLayer';
import { LayersControl } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { useLiveAssetLocations } from '@casops/store/apis/useLiveAssetLocationsApi';
import useFilters from '@casops/filters/useFilters';
import {
  RegisteredAssetListFilters,
  useListAssetsQuery,
} from '@casops/store/apis/registeredAssetsApi';
import { FilterGroup } from '@casops/filters/types';
import TracksLayerGroup from './tracks-layer-group/TracksLayerGroup';
import { OVERLAY_PORTAL_ID } from '../mapConfig';

const LARGEST_ASSET_ICON_SIZE_METRES = 96;

export const SiteMap = (): ReactElement => {
  const assetFilters = useFilters<RegisteredAssetListFilters>(FilterGroup.AssetsList);
  const geofenceFilters = useFilters<GeofenceFilters>(FilterGroup.Geofences);
  const geofenceMatch = useMatch(GEOFENCE_SUB_ROUTE);
  const shouldDisplayAssets = !geofenceMatch;
  const assetsLocations = useLiveAssetLocations();
  const { t } = useTranslation();

  const { data: geoJson, isFetching } = useGetGeofencesGeoJsonQuery(
    { query: geofenceFilters },
    {
      refetchOnMountOrArgChange: 1800,
    },
  );

  const { data } = useListAssetsQuery(
    { query: assetFilters },
    {
      skip: !shouldDisplayAssets,
      refetchOnMountOrArgChange: 60,
    },
  );
  const { items: assets } = shouldDisplayAssets ? (data ?? {}) : { items: [] };

  const { centre, zoom } = useMapLastPositionData();

  const { geofenceId: routeId } = useParams();
  const { value: searchParamId } = useSearchParamId(GEOFENCE_KEY);

  // Priortitise an explicit route id over a search param. There should be no overlap in usage.
  const geofenceId = routeId ? Number(routeId) : searchParamId;

  const isGeofenceSubRoute = Boolean(geofenceMatch);

  // Determine the create/edit mode for map editing controls from the route.
  const geofenceCreateMatch = useMatch('home/geofence/create');
  const createMode = Boolean(geofenceCreateMatch);

  const { value: assetId } = useSearchParamId(ASSET_KEY);

  // Do not zoom to fit all geofences. It is redundant after the first use, and is likely to override the user's preferred map position.
  const geofenceFocusMapProps = useMemo<Partial<MapViewProps> | undefined>(() => {
    if (geoJson && typeof geofenceId === 'number') {
      const currentGeofence = geoJson.features.find(
        (feature: Feature<Geometry, GeofenceProperties>) => feature.properties.id === geofenceId,
      );

      // Zoom in to the specific geofence.
      if (currentGeofence) {
        const { geometry } = currentGeofence;
        const { geofenceType, radius } = currentGeofence.properties;

        if (
          geometry.type === 'Point' &&
          geofenceType === 'Circle' &&
          geometry.coordinates.length === 2
        ) {
          const geofencePoint: L.LatLngExpression = [
            geometry.coordinates[1],
            geometry.coordinates[0],
          ];
          // Use the radius to calculate bounds.
          const safeRadius = radius ?? 1;
          const bounds: LatLngBounds = latLng(geofencePoint).toBounds(safeRadius * 2);
          return { bounds, padding: true };
        } else if (
          geometry.type === 'Polygon' &&
          geofenceType === 'Polygon' &&
          geometry.coordinates.length
        ) {
          const points: Position[] = geometry.coordinates.flat(1);
          const latLngPoint: L.LatLngExpression[] = points.map(point => [point[1], point[0]]);
          if (points.length > 1) {
            return { bounds: latLngBounds(latLngPoint), padding: true };
          }
        }
      }
    }

    return undefined;
  }, [geoJson, geofenceId]);

  const assetFocusMapProps = useMemo<Partial<MapViewProps> | undefined>(() => {
    if (assets && typeof assetId === 'number') {
      const currentAsset = assets.find(asset => asset.id === assetId);
      const currentAssetLocation = assetsLocations.find(
        assetsLocation => assetsLocation.id === assetId,
      );
      if (
        currentAsset &&
        currentAssetLocation &&
        typeof currentAssetLocation.lat === 'number' &&
        typeof currentAssetLocation.lon === 'number'
      ) {
        const assetPoint = latLng(currentAssetLocation.lat, currentAssetLocation.lon);
        // The buffer size accounts for the largest possible icon size plus some padding
        const bounds = assetPoint.toBounds(LARGEST_ASSET_ICON_SIZE_METRES / 2);

        return {
          bounds,
          padding: true,
        };
      }
    }

    return undefined;
  }, [assetId, assets, assetsLocations]);

  const showMap = typeof geoJson !== 'undefined';

  // Use the last map position data as a fallback if there is no valid mapProps that could be generated from the selected geofence or asset.
  // Do this outside of the useMemo hook, otherwise the mapProps will constantly change if the last position data is a dependency.
  const safeMapProps = geofenceFocusMapProps ??
    assetFocusMapProps ?? { initialCentre: centre, initialZoom: zoom };

  return (
    <div className={styles.overlayContainer}>
      <MapLoader showMap={showMap} isFetching={isFetching}>
        <MapView {...safeMapProps} showLayersControl={true} useMapProvider={useMapProvider}>
          <PersistedLocation />
          <OtherGeofencesLayerGroup
            id={geofenceId}
            // Exclude the current geofence when in a specific geofence route.
            excludeSelected={Boolean(routeId)}
          />
          {assets && assets.length > 0 && (
            <LayersControl.Overlay name={t('Model.Plural.Asset_other')} checked={true}>
              <MultipleAssetPositionLayer assets={assets} assetsLocations={assetsLocations} />
            </LayersControl.Overlay>
          )}
          {isGeofenceSubRoute && (
            <>
              <TracksLayerGroup />
              <ShapeEditor
                geofenceId={createMode ? undefined : Number(routeId)}
                createMode={createMode}
              />
            </>
          )}
        </MapView>
      </MapLoader>
      {/* Custom HTML controlled by the map interactions gets injected here (outside of the map). */}
      <div id={OVERLAY_PORTAL_ID} className={styles.overlayPortal} />
    </div>
  );
};

export default SiteMap;
